import { useCallback, useMemo, useState } from 'react'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { TooltipProvider } from '@/components/ui/tooltip'
import NovaApuracaoComModal from './nova-apuraca'
import { ProdutosStatus } from '@/lib/enums'
import { useUpdateProductStatus } from '@/lib/hooks/product'
import { InfoRelacao } from './info-relacao'
import { Apuracao } from '@/types/apuracao'
import { TypeViewProduct } from '../../state/selectProduct'
import { useApuracaoSelecionada } from '../../hooks/seApuracaoSelecionada'
import { Select, SelectTrigger, SelectContent, SelectItem, SelectValue } from '@/components/ui/select'
import { Calendar, ChevronDown } from 'lucide-react'
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from '@/components/ui/dropdown-menu'
import { friendlyNameMaps } from '@/components/form/input/StatusMapper'
import { getStatusColor, produtosStatusOptions } from '@/features/products/utils'
import { cn } from '@/lib/utils'

type ClientInfo = {
  parceiro: string
  empresa: string
  nomeAnalista: string
  status?: string
  id_client?: string
}

interface ClientInfoPanelProps {
  clientInfo: ClientInfo
  productId?: string
  type: TypeViewProduct
  onNewAssessment?: (period: string) => void
  apuracoes?: Apuracao[]
}

export default function InfoProductPanel({
  clientInfo,
  productId,
  type,
  onNewAssessment,
  apuracoes = []
}: ClientInfoPanelProps) {
  const { apuracaoSelecionada, setApuracaoSelecionada } = useApuracaoSelecionada()
  const initialStatus = (clientInfo.status as ProdutosStatus) ?? ProdutosStatus.ATIVO
  const [selectedStatus, setSelectedStatus] = useState<ProdutosStatus>(initialStatus)
  const [isProcessing, setIsProcessing] = useState(false)
  const { mutateAsync: updateStatus } = useUpdateProductStatus()

  const statusOptions = useMemo(
    () =>
      produtosStatusOptions.map(opt => ({
        value: opt.value as ProdutosStatus,
        label: friendlyNameMaps['productStatus']?.[opt.value] ?? opt.label ?? opt.value
      })),
    []
  )

  const handleStatusChange = useCallback(
    async (value: ProdutosStatus) => {
      if (!productId) return
      const prev = selectedStatus
      setSelectedStatus(value) // optimistic

      try {
        await updateStatus({ id: productId, status: value })
      } catch (err) {
        console.error('Erro ao atualizar status:', err)
        setSelectedStatus(prev) // rollback
      }
    },
    [productId, selectedStatus, updateStatus]
  )

  const handleNewAssessment = async () => {
    setIsProcessing(true)
    // aqui você já tinha um delay; mantive para sinalizar processamento
    await new Promise(res => setTimeout(res, 600))
    if (onNewAssessment && apuracaoSelecionada) {
      onNewAssessment(periodoLabel(apuracaoSelecionada))
    }
    setIsProcessing(false)
  }

  const periodoLabel = (a?: Apuracao) => {
    const competencia = Array.isArray(a?.competencias) ? a?.competencias[0] : a?.competencias
    const inicio = competencia?.competenciaStart?.trim() || ''
    const fim = competencia?.competenciaEnd?.trim() || ''

    if (inicio && fim) return `${inicio} - ${fim}`
    if (inicio) return inicio
    if (fim) return fim
    return 'Não definido'
  }

  return (
    <TooltipProvider>
      <div className="space-y-6">
        <Card className="bg-background transition-all duration-300 hover:shadow-md">
          <CardHeader className="pb-3">
            <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
              <CardTitle className="text-xl font-semibold text-foreground">Situação</CardTitle>

              {/* Pílula de status no mesmo estilo do ClientStatusTag */}
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <button
                    className={cn(
                      'flex items-center justify-between min-w-[220px] text-sm px-3 py-1 rounded-full focus:outline-none focus:ring-2 focus:ring-offset-2',
                      'transition-colors',
                      getStatusColor(selectedStatus)
                    )}
                  >
                    <span className="text-white">
                      {friendlyNameMaps['produtosStatus']?.[selectedStatus] ?? selectedStatus}
                    </span>
                    <ChevronDown className="h-4 w-4 text-white/90" />
                  </button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="w-56 max-h-60 overflow-y-auto p-0">
                  {statusOptions.map(opt => (
                    <DropdownMenuItem
                      key={opt.value}
                      onClick={() => handleStatusChange(opt.value)}
                      className="cursor-pointer"
                    >
                      {opt.label}
                    </DropdownMenuItem>
                  ))}
                </DropdownMenuContent>
              </DropdownMenu>
            </div>

            {/* bloco com relação parceiro/empresa/analista, igual visual clean */}
            <InfoRelacao clientInfo={clientInfo} type={type} productId={productId} />
          </CardHeader>

          <CardContent className="flex flex-col sm:flex-row sm:items-center sm:gap-10 gap-4 max-w-full">
            {/* Botão de nova apuração */}
            <NovaApuracaoComModal
              modalAction={handleNewAssessment}
              isProcessing={isProcessing}
              id_client={apuracaoSelecionada?.id_client ?? ''}
              id_product={apuracaoSelecionada?.id_product ?? ''}
              id_tenant={apuracaoSelecionada?.id_tenant ?? ''}
              typeServico={type}
            />

            {/* Seletor de período com layout consistente */}
            <div className="flex items-center gap-3 min-w-0">
              <Calendar className="h-4 w-4 text-primary shrink-0" />
              <span className="shrink-0 text-sm text-muted-foreground">Período:</span>
              <div className="min-w-0 w-full">
                <Select
                  value={apuracaoSelecionada?.id ?? ''}
                  onValueChange={id => {
                    const found = apuracoes.find(a => a.id === id)
                    if (found) setApuracaoSelecionada(found)
                  }}
                >
                  <SelectTrigger className="max-w-[240px] w-full bg-white border-primary/20 truncate">
                    <SelectValue placeholder="Selecione o período">
                      {periodoLabel(apuracaoSelecionada ?? undefined) || 'Selecione o período'}
                    </SelectValue>
                  </SelectTrigger>
                  <SelectContent>
                    {apuracoes.map((apur, idx) => (
                      <SelectItem key={apur.id} value={apur.id} className="truncate">
                        {`${idx + 1}. ${periodoLabel(apur)}`}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
    </TooltipProvider>
  )
}
